import type {
  CreateAccountRequest,
  CreateAccountResponse,
  PlansResponse,
  BuildPlanResponse,
  SelfServeProduct,
  ApiError,
} from "./wizardTypes";
import { runtimeConfig } from "src/services/runtimeConfig";

// In dev, use a relative path so the Vite proxy forwards to the .NET backend
// (avoids cross-port cert issues). In production, use the full runtime URL.
const API_BASE = import.meta.env.VITE_SIGNUP_API_BASE
  ?? (import.meta.env.DEV ? "/api/signup" : `${runtimeConfig.apiUrl}/api/signup`);

export class SignupApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "SignupApiError";
  }
}

async function post<TReq, TRes>(
  path: string,
  body: TReq,
): Promise<TRes> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err: ApiError = await res.json().catch(() => ({
      error: "unknown",
      message: "Something went wrong. Please try again.",
    }));
    throw new SignupApiError(err.error, err.message, res.status);
  }
  if (res.status === 204) return {} as TRes;
  return res.json();
}

async function get<TRes>(path: string): Promise<TRes> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const err: ApiError = await res.json().catch(() => ({
      error: "unknown",
      message: "Something went wrong. Please try again.",
    }));
    throw new SignupApiError(err.error, err.message, res.status);
  }
  return res.json();
}

/** Fetch the self-serve product catalogue (questionnaire checkboxes). */
export async function getProducts(): Promise<SelfServeProduct[]> {
  return get<SelfServeProduct[]>("/products");
}

/** Fetch the product catalogue and pricing tiers. */
export async function getPlans(): Promise<PlansResponse> {
  return get<PlansResponse>("/plans");
}

/** Submit questionnaire answers and get a computed plan from the server. */
export async function buildPlanApi(
  needs: string[],
  volumes: Record<string, number>,
  email: string,
): Promise<BuildPlanResponse> {
  return post<
    { needs: string[]; volumes: Record<string, number>; email: string },
    BuildPlanResponse
  >("/build-plan", { needs, volumes, email });
}

/**
 * Whether this browser is on an internal IP (SysAdminAllowedIPs). Used only to show the
 * "no-charge test signup" hint; the server decides test mode on its own.
 */
export async function getTestMode(): Promise<{ internalIp: boolean }> {
  return get<{ internalIp: boolean }>("/test-mode");
}

export async function createAccount(
  req: CreateAccountRequest,
): Promise<CreateAccountResponse> {
  return post<CreateAccountRequest, CreateAccountResponse>("/create-account", req);
}
